import React from 'react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

/**
 * Generic PayPal Smart Buttons checkout.
 *
 * Props:
 * - amount: number            -> final amount in USD
 * - referenceType: string     -> "food" | "donation" | "shabbat"
 * - referenceId: string|null  -> existing internal id (order id, etc.)
 * - description: string       -> shown to payer
 * - payload: object           -> extra data (donor / guest info) used to create pending record
 * - validate: () => bool      -> optional validator called before opening paypal
 * - beforeCreate: async () => {referenceId, payload, amount} -> optional async hook to create a local record first
 * - onSuccess: (data) => void -> called after capture completes
 */
export const PayPalCheckout = ({
  amount,
  referenceType,
  referenceId = null,
  description = 'Jabad Guadalajara 2026',
  payload = {},
  validate,
  beforeCreate,
  onSuccess,
  disabled = false,
  currency = 'USD',
}) => {
  if (!CLIENT_ID) {
    return (
      <div className="p-3 text-center text-sm text-red-600 bg-red-50 rounded-xl" data-testid="paypal-missing-key">
        PayPal no está configurado (falta REACT_APP_PAYPAL_CLIENT_ID)
      </div>
    );
  }

  return (
    <div className={disabled ? 'opacity-50 pointer-events-none' : ''} data-testid="paypal-buttons-wrapper">
      <PayPalScriptProvider
        key={currency}
        options={{
          clientId: CLIENT_ID,
          currency: currency,
          intent: 'capture',
          // Habilita botón de tarjeta (debito/credito sin cuenta PayPal).
          // venmo y paylater son SOLO US — los excluímos para evitar que el SDK
          // falle al inicializarlos con una cuenta merchant mexicana.
          'enable-funding': 'card',
          'disable-funding': 'venmo,paylater,credit',
        }}
      >
        <PayPalButtons
          style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'pay' }}
          disabled={disabled}
          forceReRender={[amount, referenceType, referenceId, currency, JSON.stringify(payload)]}
          createOrder={async () => {
            if (validate && !validate()) {
              return Promise.reject(new Error('validation-failed'));
            }
            let effectiveRefId = referenceId;
            let effectivePayload = payload;
            let effectiveAmount = Number(amount);
            if (beforeCreate) {
              try {
                const pre = await beforeCreate();
                if (pre) {
                  if (pre.referenceId) effectiveRefId = pre.referenceId;
                  if (pre.payload) effectivePayload = { ...effectivePayload, ...pre.payload };
                  if (pre.amount != null) effectiveAmount = Number(pre.amount);
                }
              } catch (e) {
                console.error('beforeCreate failed', e);
                toast.error('No se pudo preparar el pedido');
                throw e;
              }
            }
            try {
              const res = await axios.post(`${API}/paypal/create-order`, {
                reference_type: referenceType,
                reference_id: effectiveRefId,
                amount: effectiveAmount,
                currency: currency,
                description,
                ...effectivePayload,
              });
              // Guardar el reference_id real devuelto (para la captura)
              window.__lastPaypalRef = res.data.reference_id;
              return res.data.id;
            } catch (e) {
              console.error('PayPal create order failed', e);
              toast.error('No se pudo iniciar el pago con PayPal');
              throw e;
            }
          }}
          onApprove={async (data) => {
            try {
              const res = await axios.post(`${API}/paypal/capture-order`, {
                paypal_order_id: data.orderID,
                reference_type: referenceType,
                reference_id: window.__lastPaypalRef || referenceId,
              });
              if (res.data.status === 'COMPLETED') {
                toast.success('¡Pago recibido!');
                if (onSuccess) onSuccess({ ...res.data, amount });
              } else {
                toast.error('Pago no completado');
              }
            } catch (e) {
              console.error('PayPal capture failed', e);
              toast.error('Error al confirmar el pago');
            }
          }}
          onError={(err) => {
            console.error('PayPal SDK error:', err);
            const msg = (err && (err.message || String(err))) || '';
            // Detect Safari ITP / blocked third-party storage
            const looksLikeITP = /storage|cookie|third.?party|blocked|access/i.test(msg);
            if (looksLikeITP) {
              toast.error(
                'Safari está bloqueando PayPal. Por favor abre el link desde Chrome o desactiva "Impedir rastreo entre sitios" en Ajustes de Safari.',
                { duration: 8000 }
              );
            } else {
              toast.error('Error de PayPal. Intenta de nuevo o cambia de navegador (Chrome funciona mejor).', { duration: 6000 });
            }
          }}
          onCancel={() => {
            toast.info('Pago cancelado');
          }}
        />
      </PayPalScriptProvider>
    </div>
  );
};

export default PayPalCheckout;
